import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock,
  Gift,
  GraduationCap,
  LineChart,
  Search,
  TrendingUp,
  UserPlus,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";

import {
  getTeamDashboard,
  getTeamDeals,
  getTrainingTeamReport,
  type DealDashboardMetrics,
  type ManagerDealListItem,
  type TrainingTeamReportItem,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { StatCard } from "@/components/StatCard";
import { TeamInviteDialog } from "@/components/TeamInviteDialog";
import { DealExplorerDrawer } from "@/components/po-insights/DealExplorerDrawer";
import { DealMetricDrawer, type DealMetricMode } from "@/components/po-insights/DealMetricDrawer";
import { RewardsPreferencesDrawer } from "@/components/po-insights/RewardsPreferencesDrawer";
import { TeamDirectory, type TeamDirectoryHandle } from "@/components/po-insights/TeamDirectory";
import { TeamTrainingDrawer } from "@/components/po-insights/TeamTrainingDrawer";
import { PeriodSelect, type StatPeriod } from "@/components/insights/PeriodSelect";
import { deriveCertification } from "@/components/training/certification";
import { Button } from "@/components/ui/button";
import { formatAud } from "@/lib/currency";
import { isExpiringWithin } from "@/lib/dealExpiry";

/** The "Expiring Deals" card count window. MUST match its drawer (CHP-493) —
 * both count LIVE deals closing within this many days. The card and the drawer
 * share the same `isExpiringWithin` predicate (lib/dealExpiry) so the card
 * count can never diverge from the drawer's Deal Count / ledger. */
const EXPIRING_WINDOW_DAYS = 30;

/** Which drawer is currently open. The page owns this; each drawer body is
 * owned by its component (the seam). A DealMetricMode opens the deal drill-down;
 * "find-deal" opens the full Deal Explorer; "training"/"rewards" their own. */
type OpenDrawer = DealMetricMode | "find-deal" | "training" | "rewards" | null;

/**
 * /dashboard/partner/insights — the PO "Insights & Admin" surface.
 *
 * A header action row + four clickable Performance-Overview cards, each a "hot
 * zone" that opens a 65% right-side drawer: Total Sales Closed YTD, Active
 * Pipeline, Expiring Deals (all deal-backed drill-downs), and Certification
 * Status (Team Knowledge). Below sits the persistent Team Access & Activity
 * directory.
 *
 * Route registration gates roles={["channel_partner"]}; this component adds the
 * is_org_admin guard so a BDM who deep-links sees the same explanation as
 * /dashboard/partner/team rather than a half-rendered admin surface.
 */
export function PartnerInsightsRoute() {
  const me = useCurrentUser();
  const [openDrawer, setOpenDrawer] = useState<OpenDrawer>(null);

  if (me.isAuthLoading || me.isLoading) {
    return <p role="status">Loading…</p>;
  }
  if (!me.isAuthenticated || !me.data || !me.accessToken) {
    return <p role="alert">Sign in to view insights.</p>;
  }
  // Only the org's Program Owner sees this surface. BDMs who deep-link get a
  // plain explanation, not a 403 surprise (same pattern as partner/team.tsx).
  if (me.data.is_org_admin === false) {
    return (
      <p role="alert" className="p-6 text-sm text-muted-foreground">
        Only your organisation&apos;s Program Owner can view Insights &amp; Admin.
      </p>
    );
  }

  const orgName = me.data.organization?.company_name ?? null;
  const orgId = me.data.organization?.id ?? null;

  return (
    <InsightsContent
      token={me.accessToken}
      orgName={orgName}
      orgId={orgId}
      openDrawer={openDrawer}
      setOpenDrawer={setOpenDrawer}
    />
  );
}

interface InsightsContentProps {
  token: string;
  orgName: string | null;
  orgId: string | null;
  openDrawer: OpenDrawer;
  setOpenDrawer: (next: OpenDrawer) => void;
}

function InsightsContent({
  token,
  orgName,
  orgId,
  openDrawer,
  setOpenDrawer,
}: InsightsContentProps) {
  // po-dashboard: the header CTA opens the team-invite modal in place (was a
  // deep-link to /dashboard/partner/team, which still works on its own route).
  const [inviteOpen, setInviteOpen] = useState(false);
  // The Team Directory below the cards holds the search box; the header's
  // "Find Team Member" action scrolls to + focuses it.
  const directoryRef = useRef<TeamDirectoryHandle>(null);

  // The deal cards read the org-wide team dashboard aggregate.
  const teamDashboard = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", "team-dashboard", token],
    queryFn: () => getTeamDashboard(token),
    enabled: !!token,
  });
  const metrics = teamDashboard.data;

  // The Expiring Deals card count is computed CLIENT-SIDE from the same
  // org-scoped feed the Expiring drawer uses — LIVE + closing ≤30 days — so the
  // card matches the drawer (the dashboard's expiring_soon is a 14-day metric).
  const teamDeals = useQuery<ManagerDealListItem[]>({
    queryKey: ["deals", "team-list", "all", token],
    queryFn: () => getTeamDeals(token, "all"),
    enabled: !!token,
  });
  const expiringCount = useMemo(() => {
    // CHP-804: distinguish a fetch error from loading/zero — both previously
    // showed "—", so a failed query read as "no expiring deals".
    if (teamDeals.isError) return "Error" as const;
    if (!teamDeals.data) return "—" as const;
    const now = Date.now();
    return teamDeals.data.filter((d) => isExpiringWithin(d, now, EXPIRING_WINDOW_DAYS)).length;
  }, [teamDeals.data, teamDeals.isError]);

  // The Certification Status card reads the org-wide training report and shows
  // the share of the team that is fully certified.
  const trainingReport = useQuery<TrainingTeamReportItem[]>({
    queryKey: ["training", "team-report"],
    queryFn: () => getTrainingTeamReport(token),
    enabled: !!token,
  });
  const certifiedPct = useMemo(() => {
    // CHP-804: distinguish a fetch error from loading/zero — "—" on error read
    // as "no data" rather than a failed load.
    if (trainingReport.isError) return "Error" as const;
    const rows = trainingReport.data;
    if (!rows) return "—" as const;
    if (rows.length === 0) return "0%";
    const certified = rows.filter((r) => deriveCertification(r) === "certified").length;
    return `${Math.round((certified / rows.length) * 100)}%`;
  }, [trainingReport.data, trainingReport.isError]);

  // Team sales closed — closed-won value/count over the selected window
  // (CHP-842: This month / This quarter / This year, seeded to year).
  const [salesPeriod, setSalesPeriod] = useState<StatPeriod>("year");
  const salesFigures = metrics
    ? {
        month: {
          value: metrics.closed_won_value_month,
          count: metrics.closed_won_count_month,
        },
        quarter: {
          value: metrics.closed_won_value_quarter,
          count: metrics.closed_won_count_quarter,
        },
        year: { value: metrics.closed_won_value_ytd, count: metrics.closed_won_count_ytd },
      }[salesPeriod]
    : null;
  const totalSalesValue = salesFigures ? formatAud(salesFigures.value) : "—";
  const totalSalesSubtitle = salesFigures
    ? `${salesFigures.count} deal${salesFigures.count === 1 ? "" : "s"} won`
    : undefined;
  // Active Pipeline — LIVE-deal value (cents) + live-deal volume. Uses
  // active_deals_value (LIVE only), not open_pipeline_value (which also sums
  // pending/extension/win-pending), so the card matches CHP-491's "# live deals"
  // spec AND its drill-down drawer's LIVE-only Total-Pipeline-Value sub-card.
  const pipelineValue = metrics ? formatAud(metrics.active_deals_value) : "—";
  // CHP-826: spell out "deals" after the live count.
  const pipelineSubtitle = metrics ? `${metrics.active_deals} live deals` : undefined;
  const expiringValue =
    typeof expiringCount === "number" && expiringCount > 0 ? "closing ≤30 days" : undefined;

  return (
    <div className="container py-6">
      {/* CHP-490: header copy + action layout mirror DealDashboardHeader — the
          title + descriptor stack, then a flex-wrap action ROW sits UNDERNEATH
          (was a right-aligned button cluster beside the title). CHP-824: the
          title leads with the Channel Partner (org) name when known —
          "[Channel Partner Name] performance, at a glance." — falling back to a
          plain title for orgs with no company name. */}
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {orgName ? `${orgName} performance, at a glance.` : "Performance, at a glance."}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Track team-wide results, program growth and manage user profiles.
        </p>
      </header>
      {/* Review-r2: buttons are sentence case ("Invite new team member", etc.). */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setInviteOpen(true)} data-testid="insights-invite-team">
          <UserPlus className="h-4 w-4" aria-hidden /> Invite new team member
        </Button>
        {/* Find Deal opens the full Deal Explorer; the search box inside the
            drawer narrows by client / deal ID / owner. */}
        <Button
          variant="outline"
          onClick={() => setOpenDrawer("find-deal")}
          data-testid="insights-find-deal"
        >
          <Search className="h-4 w-4" aria-hidden /> Find deal
        </Button>
        {/* Find Team Member scrolls to + focuses the Team Directory search. */}
        <Button
          variant="outline"
          onClick={() => directoryRef.current?.focusSearch()}
          data-testid="insights-find-team-member"
        >
          <Search className="h-4 w-4" aria-hidden /> Find team member
        </Button>
        {/* CHP-487: rewards-model preference editor — last in the action row. */}
        <Button
          variant="outline"
          onClick={() => setOpenDrawer("rewards")}
          data-testid="insights-update-rewards"
        >
          <Gift className="h-4 w-4" aria-hidden /> Update reward preferences
        </Button>
      </div>

      <section className="mt-8">
        <h2 className="text-lg font-semibold tracking-tight">Performance overview</h2>
        {/* CHP-804: a failed metric query must read as an error, not a zero/"—".
            Mirrors the manager-insights dashboard error banner. */}
        {teamDashboard.isError || teamDeals.isError || trainingReport.isError ? (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
            data-testid="insights-overview-error"
          >
            Couldn&apos;t load some performance metrics. Please refresh to try again.
          </div>
        ) : null}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* CHP-491: Total Sales Closed → Total Sales drill-down (CHP-495).
              CHP-842: in-card period picker (month/quarter/year, seeded year). */}
          <StatCard
            title="Team sales closed"
            value={totalSalesValue}
            subtitle={totalSalesSubtitle}
            icon={TrendingUp}
            testId="insights-total-sales"
            // Genuine positive achievement — teal numeral.
            tone="success"
            onClick={() => setOpenDrawer("total-sales")}
            action={
              <PeriodSelect
                value={salesPeriod}
                onChange={setSalesPeriod}
                testId="insights-total-sales-period"
                ariaLabel="Sales closed period"
              />
            }
          />
          {/* Active Pipeline → Active Pipeline drill-down (CHP-494). */}
          <StatCard
            title="Team pipeline"
            value={pipelineValue}
            subtitle={pipelineSubtitle}
            icon={LineChart}
            testId="insights-active-pipeline"
            tone="neutral"
            onClick={() => setOpenDrawer("active-pipeline")}
          />
          {/* Expiring Deals → Expiring drill-down (CHP-493). */}
          <StatCard
            title="Expiring deals"
            value={expiringCount}
            subtitle={expiringValue}
            icon={CalendarClock}
            testId="insights-expiring-deals"
            // Attention metric — colour by exception (amber left edge) only
            // when deals are actually expiring.
            tone="neutral"
            accent={typeof expiringCount === "number" && expiringCount > 0 ? "amber" : undefined}
            onClick={() => setOpenDrawer("expiring")}
          />
          {/* Certification Status → Team Knowledge drill-down (CHP-496). */}
          <StatCard
            title="Team certification"
            value={certifiedPct}
            subtitle="certified"
            icon={GraduationCap}
            testId="insights-certification"
            tone="neutral"
            onClick={() => setOpenDrawer("training")}
          />
        </div>
      </section>

      {/* The persistent Team Management Workspace — a full-width directory of the
          org's members below the metric cards. The header's "Find Team Member"
          action drives this via directoryRef. */}
      <TeamDirectory ref={directoryRef} token={token} />

      {/* The page owns open/close state; each drawer owns its own body. */}
      <DealMetricDrawer
        mode={
          openDrawer === "total-sales" ||
          openDrawer === "active-pipeline" ||
          openDrawer === "expiring"
            ? openDrawer
            : null
        }
        onClose={() => setOpenDrawer(null)}
      />
      <DealExplorerDrawer
        open={openDrawer === "find-deal"}
        onClose={() => setOpenDrawer(null)}
        initialFilter="registrations"
      />
      <TeamTrainingDrawer open={openDrawer === "training"} onClose={() => setOpenDrawer(null)} />
      <RewardsPreferencesDrawer
        open={openDrawer === "rewards"}
        token={token}
        onClose={() => setOpenDrawer(null)}
      />

      <TeamInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        token={token}
        orgName={orgName}
        orgId={orgId}
      />
    </div>
  );
}
